import { InputRule, Node as TiptapNode } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import {
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";

import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { VariableDropdown } from "./VariableDropdown";

// Add module declaration for custom commands
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    variable: {
      insertVariable: (variableName: string) => ReturnType;
    };
  }
}

// Configuration for variables
export interface VariableConfig {
  [key: string]: string; // variable name -> display value
}

interface VariableOptions {
  variables?: VariableConfig;
}

interface VariableNodeAttributes {
  name: string;
  value: string;
}

function VariableNodeComponent(props: NodeViewProps) {
  const { node, editor, getPos } = props;
  const extension = editor.extensionManager.extensions.find(
    (ext) => ext.name === "variable",
  );
  const variables = (extension?.options as VariableOptions)?.variables || {};

  const handleVariableChange = (newVariableName: string) => {
    if (typeof getPos === "function") {
      const pos = getPos();
      const nodeSize = node.nodeSize;
      const tr = editor.state.tr;

      // Update the node
      tr.setNodeMarkup(pos, undefined, {
        name: newVariableName,
        value: variables[newVariableName],
      });

      // Move cursor to after the node
      tr.setSelection(TextSelection.create(tr.doc, pos + nodeSize));

      editor.view.dispatch(tr);
      editor.view.focus();
    }
  };

  return (
    <NodeViewWrapper as="span" style={{ display: "inline" }}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <span
            contentEditable={false}
            className="inline-block bg-green-100 text-green-900 px-1 py-0.5 rounded text-sm font-medium mx-0.5 cursor-pointer select-none align-baseline leading-tight hover:bg-green-200 transition-colors"
          >
            {(node.attrs as VariableNodeAttributes).value ||
              (node.attrs as VariableNodeAttributes).name}
          </span>
        </DropdownMenuTrigger>
        <VariableDropdown
          variables={variables}
          onSelect={handleVariableChange}
          currentVariable={(node.attrs as VariableNodeAttributes).name}
        />
      </DropdownMenu>
    </NodeViewWrapper>
  );
}

export const Variable = TiptapNode.create<VariableOptions>({
  name: "variable",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return {
      variables: {},
    };
  },

  addAttributes() {
    return {
      name: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-variable-name"),
        renderHTML: (attributes) => ({
          "data-variable-name": attributes.name,
        }),
      },
      value: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-variable-value"),
        renderHTML: (attributes) => ({
          "data-variable-value": attributes.value,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-variable-name]",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableNodeComponent);
  },

  addInputRules() {
    const options = this.options;
    return [
      new InputRule({
        // Match {{ variable_name }} pattern with optional spaces and case-insensitive
        find: /\{\{\s*(\w+)\s*\}\}$/,
        handler({ state, range, match }) {
          const rawVariableName = match[1];
          const variables = options.variables || {};

          // Find matching variable (case-insensitive)
          const variableKey = Object.keys(variables).find(
            (key) => key.toLowerCase() === rawVariableName.toLowerCase(),
          );

          // If no variables configured or variable not found, don't convert
          if (!variableKey) {
            return null;
          }

          const { tr } = state;
          const start = range.from;
          const end = range.to;

          // Replace the matched text with our variable node
          tr.replaceRangeWith(
            start,
            end,
            state.schema.nodes.variable.create({
              name: variableKey,
              value: variables[variableKey],
            }),
          );
        },
      }),
    ];
  },

  addCommands() {
    const options = this.options;
    return {
      insertVariable:
        (variableName: string) =>
        ({ commands }) => {
          const variables = options.variables || {};

          // Find matching variable (case-insensitive)
          const variableKey = Object.keys(variables).find(
            (key) => key.toLowerCase() === variableName.toLowerCase(),
          );

          if (!variableKey) {
            return false;
          }

          return commands.insertContent([
            {
              type: "variable",
              attrs: {
                name: variableKey,
                value: variables[variableKey],
              },
            },
            {
              type: "text",
              text: " ",
            },
          ]);
        },
    };
  },
});

// Helper function to parse content and convert {{variables}} to nodes
export function parseVariables(
  html: string,
  variables: VariableConfig = {},
): string {
  // This function would be used to pre-process HTML content
  // to convert existing {{variable}} syntax to our node format
  // Support both {{var}} and {{ var }} with optional spaces
  return html.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, rawVariableName) => {
    // Find matching variable (case-insensitive)
    const variableKey = Object.keys(variables).find(
      (key) => key.toLowerCase() === rawVariableName.toLowerCase(),
    );

    if (variableKey) {
      return `<span data-variable-name="${variableKey}" data-variable-value="${variables[variableKey]}"></span>`;
    }
    return match;
  });
}

// Helper function to serialize content back to {{variables}} syntax
export function serializeVariables(html: string): string {
  // Convert our variable nodes back to {{variable}} syntax for storage
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const variableNodes = doc.querySelectorAll("span[data-variable-name]");
  variableNodes.forEach((node) => {
    const variableName = node.getAttribute("data-variable-name");
    if (variableName) {
      const textNode = doc.createTextNode(`{{${variableName}}}`);
      node.parentNode?.replaceChild(textNode, node);
    }
  });

  return doc.body.innerHTML;
}
